import {
  DEFAULT_TELEHEALTH_CONTACT_TYPE,
  TELEHEALTH_CONTACT_TYPE,
  type TelehealthContactType,
} from '@/lib/constants';

export type SelfScheduleVisitKind = 'visit' | 'phone call' | 'video call';

export type SelfScheduleCalendarTypes = {
  appointmentTypeId: string;
  contactType: TelehealthContactType;
  additionalAppointmentTypeId?: string;
  additionalContactType?: TelehealthContactType;
  visitKind: SelfScheduleVisitKind;
};

export type SelfScheduleCalendarDefaults = {
  defaultAppointmentTypeId: string;
  defaultContactType: TelehealthContactType;
  additionalAppointmentTypeId: string;
  additionalContactType: TelehealthContactType;
};

type ResolveSelfScheduleCalendarTypesInput = {
  appointmentTypeId?: string;
  contactType?: TelehealthContactType;
};

function resolveVisitKind(
  contactType: TelehealthContactType,
  additionalContactType?: TelehealthContactType
): SelfScheduleVisitKind {
  if (additionalContactType != null && additionalContactType !== contactType) {
    return 'visit';
  }
  if (contactType === TELEHEALTH_CONTACT_TYPE.PHONE) {
    return 'phone call';
  }
  return 'video call';
}

/**
 * Resolves AvailabilityCalendar props for self-schedule.
 * When `appointmentTypeId` is present (V2 search override), uses a single type and
 * omits the additional video+phone merge source. Explicit `contactType` wins over the
 * appointment-type map.
 */
export function resolveSelfScheduleCalendarTypes(
  { appointmentTypeId, contactType }: ResolveSelfScheduleCalendarTypesInput,
  defaults: SelfScheduleCalendarDefaults
): SelfScheduleCalendarTypes {
  const contactTypeByAppointmentTypeId: Record<string, TelehealthContactType> = {
    [defaults.additionalAppointmentTypeId]: defaults.additionalContactType,
    [defaults.defaultAppointmentTypeId]: defaults.defaultContactType,
  };

  const resolvedAppointmentTypeId = appointmentTypeId ?? defaults.defaultAppointmentTypeId;

  let resolvedContactType: TelehealthContactType = defaults.defaultContactType;
  if (contactType != null) {
    resolvedContactType = contactType;
  } else if (appointmentTypeId != null) {
    resolvedContactType =
      contactTypeByAppointmentTypeId[appointmentTypeId] ?? DEFAULT_TELEHEALTH_CONTACT_TYPE;
  }

  if (appointmentTypeId) {
    return {
      appointmentTypeId: resolvedAppointmentTypeId,
      contactType: resolvedContactType,
      visitKind: resolveVisitKind(resolvedContactType),
    };
  }

  return {
    appointmentTypeId: resolvedAppointmentTypeId,
    contactType: resolvedContactType,
    additionalAppointmentTypeId: defaults.additionalAppointmentTypeId,
    additionalContactType: defaults.additionalContactType,
    visitKind: resolveVisitKind(resolvedContactType, defaults.additionalContactType),
  };
}
